import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./Hero.module.css";
import { spotlightContent } from "../../Content/spotlight";
import { Navdots } from "./Navdots/Navdots";
import { buildFilterCSS, buildPositionCSS } from "../MediaEditor/MediaEditor";
import siteConfig from "../../config/site";
import { Link } from "react-router-dom";

const API_URL = siteConfig.apiUrl;

// Gradient placeholders when no videos/images
const gradientPlaceholders = [
  "linear-gradient(135deg, #0a0a0f 0%, #1a0533 30%, #7c3aed 60%, #d4a017 100%)",
  "linear-gradient(135deg, #0a0a0f 0%, #2d1054 30%, #a855f7 60%, #d4a017 100%)",
  "linear-gradient(135deg, #0a0a0f 0%, #1a0533 40%, #d4a017 70%, #7c3aed 100%)",
];

export const Hero = ({ handleClickedId }) => {
  const [videos, setVideos] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [fadePhase, setFadePhase] = useState("visible"); // visible | fading-out | black | fading-in
  const [useVideos, setUseVideos] = useState(false);

  const videoRefA = useRef(null);
  const videoRefB = useRef(null);
  const [activePlayer, setActivePlayer] = useState("A"); // which video element is showing
  const fadeTimer = useRef(null);

  // Fallback to image-based hero
  const [imageIndex, setImageIndex] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const [imageError, setImageError] = useState({});
  const imageTimer = useRef(null);

  // Fetch hero videos from API
  useEffect(() => {
    fetch(`${API_URL}/media/hero`)
      .then(r => r.json())
      .then(data => {
        if (data.videos?.length > 0) {
          setVideos(data.videos);
          setUseVideos(true);
        }
      })
      .catch(() => {});
  }, []);

  // Image cycling fallback (when no videos)
  useEffect(() => {
    if (useVideos) return;
    imageTimer.current = setInterval(() => {
      if (userInteracted) {
        setUserInteracted(false);
        return;
      }
      setImageIndex(prev => (prev + 1) % spotlightContent.length);
    }, 5000);
    return () => clearInterval(imageTimer.current);
  }, [useVideos, userInteracted]);

  // Video ended → fade to black → load next → fade in
  const handleVideoEnd = useCallback(() => {
    if (videos.length <= 1) {
      // Single video — just loop
      const ref = activePlayer === "A" ? videoRefA : videoRefB;
      if (ref.current) {
        ref.current.currentTime = 0;
        ref.current.play().catch(() => {});
      }
      return;
    }

    // Start fade to black
    setFadePhase("fading-out");

    fadeTimer.current = setTimeout(() => {
      setFadePhase("black");

      // Switch to next video
      const nextIdx = (currentIdx + 1) % videos.length;
      setCurrentIdx(nextIdx);

      // Load into the inactive player
      const nextPlayer = activePlayer === "A" ? "B" : "A";
      const nextRef = nextPlayer === "A" ? videoRefA : videoRefB;

      if (nextRef.current) {
        nextRef.current.src = videos[nextIdx].url;
        nextRef.current.load();

        const onCanPlay = () => {
          nextRef.current.removeEventListener("canplay", onCanPlay);
          setActivePlayer(nextPlayer);

          // Fade in from black
          setTimeout(() => {
            setFadePhase("fading-in");
            nextRef.current.play().catch(() => {});

            setTimeout(() => {
              setFadePhase("visible");
            }, 1000);
          }, 300);
        };

        nextRef.current.addEventListener("canplay", onCanPlay);
      }
    }, 1000); // 1s fade to black
  }, [videos, currentIdx, activePlayer]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
    };
  }, []);

  // Get current video's filter/position styles
  const currentVideo = videos[currentIdx];
  const videoFilter = currentVideo ? buildFilterCSS(currentVideo.filters) : "none";
  const videoPosition = currentVideo ? buildPositionCSS(currentVideo.position) : "50% 50%";

  // Start first video
  useEffect(() => {
    if (!useVideos || videos.length === 0) return;
    const ref = videoRefA.current;
    if (ref) {
      ref.src = videos[0].url;
      ref.load();
      ref.play().catch(() => {});
    }
  }, [useVideos, videos]);

  const link = { url: siteConfig.hero.defaultLink, buttonText: siteConfig.hero.buttonText };

  // ── Video Hero ──
  if (useVideos && videos.length > 0) {
    return (
      <div className={`${styles.hero} -z-50`}>
        <div className="max-h-[55vh] sm:max-h-[70vh] relative overflow-hidden">
          {/* Black overlay for fade transitions */}
          <div
            className="absolute inset-0 z-20 bg-black pointer-events-none"
            style={{
              opacity:
                fadePhase === "fading-out" ? 1 :
                fadePhase === "black" ? 1 :
                fadePhase === "fading-in" ? 0 : 0,
              transition: fadePhase === "fading-out" ? "opacity 1s ease-in" :
                          fadePhase === "fading-in" ? "opacity 1s ease-out" :
                          "none",
            }}
          />

          {/* Video Player A */}
          <video
            ref={videoRefA}
            className="absolute inset-0 object-cover w-full h-[55vh] sm:h-[70vh]"
            style={{
              opacity: activePlayer === "A" ? 1 : 0,
              transition: "opacity 0.3s",
              filter: activePlayer === "A" ? videoFilter : "none",
              objectPosition: activePlayer === "A" ? videoPosition : "50% 50%",
            }}
            muted
            playsInline
            onEnded={activePlayer === "A" ? handleVideoEnd : undefined}
          />

          {/* Video Player B */}
          <video
            ref={videoRefB}
            className="absolute inset-0 object-cover w-full h-[55vh] sm:h-[70vh]"
            style={{
              opacity: activePlayer === "B" ? 1 : 0,
              transition: "opacity 0.3s",
              filter: activePlayer === "B" ? videoFilter : "none",
              objectPosition: activePlayer === "B" ? videoPosition : "50% 50%",
            }}
            muted
            playsInline
            onEnded={activePlayer === "B" ? handleVideoEnd : undefined}
          />

          {/* Gradient overlay */}
          <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />

          {/* Content overlay */}
          <div className="absolute inset-0 z-30 flex items-end">
            <div className="px-6 pb-20 sm:pb-24">
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-2 tracking-tight font-heading">
                {siteConfig.tagline}
              </h1>
              <p className="text-base sm:text-lg text-gray-300 max-w-xl font-body">
                {siteConfig.description.split(".")[0]}.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="absolute bottom-4 left-4 sm:bottom-5 sm:left-6 z-30">
            <Link to={link.url || "/book"}>
              <button className="px-6 py-2.5 sm:px-8 sm:py-3 rounded-full bg-gold text-black font-bold text-base sm:text-xl hover:bg-gold-light transition-all duration-300 shadow-lg shadow-gold/30">
                {link.buttonText}
              </button>
            </Link>
          </div>

          {/* Video indicator dots */}
          {videos.length > 1 && (
            <div className="absolute bottom-4 right-4 sm:bottom-5 sm:right-6 z-30 flex gap-2">
              {videos.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all duration-500 ${
                    i === currentIdx ? "bg-gold w-6" : "bg-white/40"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Image/Gradient Fallback Hero ──
  const current = spotlightContent[imageIndex] || {};
  const showGradient = imageError[imageIndex] || !current.heroBanner;

  return (
    <div className={`${styles.hero} -z-50`}>
      <div className="max-h-[55vh] sm:max-h-[70vh] relative">
        {showGradient ? (
          <div
            className="h-[55vh] sm:h-[70vh] w-screen flex items-center justify-center"
            style={{ background: gradientPlaceholders[imageIndex % gradientPlaceholders.length] }}
          >
            <div className="text-center px-6">
              <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold text-white mb-4 tracking-tight font-heading">
                {current.title || siteConfig.tagline}
              </h1>
              <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto font-body">
                {current.subtitle || siteConfig.description}
              </p>
            </div>
          </div>
        ) : (
          <div className="relative">
            <picture>
              <source media="(max-width: 640px)" srcSet={current.heroBannerMobile || current.heroBanner} />
              <img
                className="object-cover object-center h-[55vh] sm:h-[70vh] w-screen"
                alt={current.title || ""}
                src={current.heroBanner}
                onError={() => setImageError(prev => ({ ...prev, [imageIndex]: true }))}
              />
            </picture>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex items-end">
              <div className="px-6 pb-20 sm:pb-24">
                <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-2 tracking-tight font-heading">
                  {current.title || siteConfig.tagline}
                </h1>
                <p className="text-base sm:text-lg text-gray-300 max-w-xl font-body">
                  {current.subtitle || ""}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="relative -left-4 bottom-10 pr-4 text-right">
          <Navdots
            content={spotlightContent}
            image={imageIndex}
            setImage={setImageIndex}
            setUserInteracted={setUserInteracted}
          />
        </div>

        <div className="relative">
          <Link to={link.url || "/book"}>
            <button className="left-4 bottom-4 sm:bottom-5 sm:left-6 px-6 py-2.5 sm:px-8 sm:py-3 absolute rounded-full bg-gold text-black font-bold text-base sm:text-xl hover:bg-gold-light transition-all duration-300 shadow-lg shadow-gold/30">
              {link.buttonText}
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};
